import React, {useCallback, useMemo} from 'react';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import {IconButton, Tooltip} from '@mui/material';
import {Button, designSystemToken, Icon} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import Dropdown, {DropdownOption} from '@/components/dropdown/Dropdown';
import styles from './ColumnSelector.module.scss';

export interface ColumnDefinition<T extends string = string> {
  id: T;
  label: string;
  locked?: boolean;
  canPin?: boolean;
}

interface ColumnSelectorProps<T extends string = string> {
  testID?: string;
  columns: ColumnDefinition<T>[];
  visibleColumns: Record<string, boolean>;
  canPinColumns?: boolean;
  pinnedColumns: T[];
  onColumnVisibilityChange: (columnId: T, visible: boolean) => void;
  onColumnPinChange: (columnId: T, pinned: boolean) => void;
  /** Called when the column selector button is opened. */
  onOpen?: (event: React.MouseEvent<HTMLElement>) => void;
  onClose?: (event: React.SyntheticEvent | Event) => void;
}

function getPinTooltipTitle(
  isAlwaysPinned: boolean,
  isPinned: boolean
): string {
  if (isAlwaysPinned) return 'Always pinned';
  if (isPinned) return 'Unpin column';
  return 'Pin column to left';
}

interface ColumnSelectorPinButtonProps<T extends string = string> {
  columnId: T;
  testID: string;
  isPinned: boolean;
  isAlwaysPinned: boolean;
  canTogglePin: boolean;
  onPinToggle: (event: React.MouseEvent, columnId: T) => void;
}

function ColumnSelectorPinButton<T extends string = string>({
  columnId,
  testID,
  isPinned,
  isAlwaysPinned,
  canTogglePin,
  onPinToggle,
}: ColumnSelectorPinButtonProps<T>) {
  return (
    <Tooltip
      title={translate(getPinTooltipTitle(isAlwaysPinned, isPinned))}
      arrow
    >
      <span>
        <IconButton
          className={styles.pinButton}
          onClick={(e) => canTogglePin && onPinToggle(e, columnId)}
          size="small"
          disabled={!canTogglePin || isAlwaysPinned}
          data-testid={`${testID}-pin-${columnId}`}
        >
          {isPinned ? (
            <PushPinIcon
              className={`${styles.pushPinIcon} ${
                isAlwaysPinned
                  ? styles.pushPinIconTertiary
                  : styles.pushPinIconPrimary
              }`}
            />
          ) : (
            <PushPinOutlinedIcon className={styles.pushPinOutlinedIcon} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function ColumnSelector<T extends string = string>({
  testID = 'column-selector',
  columns,
  visibleColumns,
  canPinColumns = true,
  pinnedColumns,
  onColumnVisibilityChange,
  onColumnPinChange,
  onOpen,
  onClose,
}: ColumnSelectorProps<T>) {
  const handleColumnToggle = useCallback(
    (columnId: T) => {
      onColumnVisibilityChange(columnId, !visibleColumns[columnId]);

      const isColumnVisibleAndPinned =
        visibleColumns[columnId] && pinnedColumns.includes(columnId);
      if (isColumnVisibleAndPinned) {
        onColumnPinChange(columnId, false);
      }
    },
    [visibleColumns, pinnedColumns, onColumnVisibilityChange, onColumnPinChange]
  );

  const handlePinToggle = useCallback(
    (event: React.MouseEvent, columnId: T) => {
      event.stopPropagation();
      const newColumnPinned = !pinnedColumns.includes(columnId);
      onColumnPinChange(columnId, newColumnPinned);
    },
    [pinnedColumns, onColumnPinChange]
  );

  const selectedIds = useMemo(
    () =>
      columns
        .filter((col) => visibleColumns[col.id] !== false)
        .map((col) => col.id),
    [columns, visibleColumns]
  );

  const visibleCount = selectedIds.length;
  const pinnedCount = pinnedColumns.length;

  const options: DropdownOption[] = useMemo(
    () =>
      columns.map((column) => {
        const isVisible = visibleColumns[column.id] !== false;
        const isPinned = pinnedColumns.includes(column.id);
        const isAlwaysPinned = Boolean(column.locked && isPinned);
        const canTogglePin = column.canPin !== false && isVisible;

        return {
          id: column.id,
          label: column.label,
          disabled: column.locked,
          endIcon: canPinColumns ? (
            <ColumnSelectorPinButton
              columnId={column.id}
              testID={testID}
              isPinned={isPinned}
              isAlwaysPinned={isAlwaysPinned}
              canTogglePin={canTogglePin}
              onPinToggle={handlePinToggle}
            />
          ) : undefined,
        };
      }),
    [
      columns,
      visibleColumns,
      pinnedColumns,
      canPinColumns,
      testID,
      handlePinToggle,
    ]
  );

  const handleChange = useCallback(
    (option: DropdownOption) => {
      handleColumnToggle(option.id as T);
    },
    [handleColumnToggle]
  );

  return (
    <Dropdown
      label={`(${visibleCount})${pinnedCount > 0 ? ` · ${translate('pinned count', {count: pinnedCount})}` : ''}`}
      title={translate('Toggle Columns')}
      subtitle={
        canPinColumns
          ? translate(
              'Pin columns to keep them visible on the left when scrolling'
            )
          : undefined
      }
      options={options}
      selectedIds={selectedIds}
      onChange={handleChange}
      selectionMode="multiCheckboxes"
      showItemDividers={false}
      onOpen={onOpen}
      onClose={onClose}
      disableSelectedBackground
      renderTrigger={({onClick}) => (
        <Button
          testID={testID}
          size="small"
          variant="text"
          onClick={onClick}
          startIcon={
            <Icon
              appearance="neutral"
              name="Navigation-Columns"
              color={designSystemToken('semantic.fg.neutral')}
              className={styles.columnsIcon}
            />
          }
        >
          ({visibleCount})
          {pinnedCount > 0 &&
            ` · ${translate('pinned count', {count: pinnedCount})}`}
        </Button>
      )}
    />
  );
}

export default ColumnSelector;
